/**
 * Utilitaire de résolution de routes : traduit un `lien` backend (style API
 * Django REST) en une route admin SPA valide.
 *
 * Le backend stocke des URLs relatives du style `/billing/2/` ou
 * `/contracts/devis/3/`. Ces chemins n'existent pas dans le SPA admin —
 * il faut les mapper vers les routes React Router réelles.
 *
 * Ordre des règles : du plus spécifique au plus générique (ex. `preuves`
 * avant le `/billing/{id}/` générique, `devis`/`remplacements` avant
 * `/contracts/{id}/`).
 */

/** Routes admin connues (sans paramètre dynamique de détail). */
const KNOWN_ADMIN_ROUTES: readonly string[] = [
  '/dashboard',
  '/clients',
  '/clients/demandes',
  '/contrats',
  '/contrats/a-signer',
  '/devis',
  '/factures',
  '/factures/a-valider',
  '/factures/paiements',
  '/finance',
  '/missions',
  '/paie',
  '/personnel',
  '/pointages',
  '/qualite',
  '/remplacements',
  '/services',
  '/services/demandes',
  '/signalements',
  '/demandes',
  '/evaluations',
  '/messagerie',
];

/**
 * Normalise un lien backend en supprimant le trailing slash optionnel, puis
 * mappe vers la route admin correspondante.
 *
 * Retourne `null` si aucune règle ne correspond — dans ce cas, le composant
 * doit uniquement marquer la notification comme lue, sans naviguer.
 */
export function resolveRoute(lien: string | null | undefined): string | null {
  if (lien === null || lien === undefined || lien.trim() === '') {
    return null;
  }

  // Normalise : supprime le trailing slash pour simplifier les comparaisons.
  const path = lien.trim().replace(/\/+$/, '');

  // ── Règle 1 : correspondance exacte avec une route admin connue ───────────
  // Si le lien (sans trailing slash) correspond exactement à une route admin
  // connue, on la retourne directement.
  // NOTE : les sous-chemins avec querystring (ex. `/missions?statut=X`) ou
  // avec segment dynamique (ex. `/contrats/ctr-1`) sont traités par les règles
  // suivantes afin de n'exposer que des routes SPA valides.
  if ((KNOWN_ADMIN_ROUTES as readonly string[]).includes(path)) {
    return path;
  }

  // ── Règle 2 : /billing/?statut=BROUILLON ──────────────────────────────
  if (/^\/billing\/?\?.*statut=BROUILLON/.test(lien.trim())) {
    return '/factures/a-valider';
  }

  // ── Règle 3 : /billing/preuves/{id}/ (preuves avant billing/{id}/) ────
  if (/^\/billing\/preuves\//.test(path)) {
    return '/factures/paiements';
  }

  // ── Règle 4 : /billing/{id}/ (facture précise → liste) ────────────────
  if (/^\/billing\/[^/]+$/.test(path)) {
    return '/factures';
  }

  // ── Règle 5 : /billing/ (liste générique) ─────────────────────────────
  if (path === '/billing') {
    return '/factures';
  }

  // ── Règle 6 : /contracts/devis/{id}/ (avant /contracts/{id}/) ─────────
  if (/^\/contracts\/devis\//.test(path)) {
    return '/devis';
  }

  // ── Règle 7 : /contracts/remplacements/{id}/ ──────────────────────────
  if (/^\/contracts\/remplacements\//.test(path)) {
    return '/remplacements';
  }

  // ── Règle 8 : /contracts/{id}/ → /contrats/{id} ──────────────────────
  const contractsMatch = /^\/contracts\/([^/]+)$/.exec(path);
  if (contractsMatch !== null) {
    return `/contrats/${contractsMatch[1]}`;
  }

  // ── Règle 9 : /contrats/{id} (déjà en français) ──────────────────────
  // Chemin déjà en français mais non capturé par la liste connue (id dynamique).
  const contratsMatch = /^\/contrats\/([^/]+)$/.exec(path);
  if (contratsMatch !== null) {
    return `/contrats/${contratsMatch[1]}`;
  }

  // ── Règle 10 : /personnel/{id}/ → /personnel/{id} ────────────────────
  const personnelMatch = /^\/personnel\/([^/]+)$/.exec(path);
  if (personnelMatch !== null) {
    return `/personnel/${personnelMatch[1]}`;
  }

  // ── Règle 11 : /clients/{id}/ → /clients/{id} ────────────────────────
  const clientsMatch = /^\/clients\/([^/]+)$/.exec(path);
  if (clientsMatch !== null) {
    return `/clients/${clientsMatch[1]}`;
  }

  // ── Règle 12 : /missions... (avec querystring éventuel) ───────────────
  if (path.startsWith('/missions')) {
    return '/missions';
  }

  // ── Règle 13 : /paie ──────────────────────────────────────────────────
  if (path === '/paie') {
    return '/paie';
  }

  // ── Règle 14 : /demandes ──────────────────────────────────────────────
  if (path === '/demandes') {
    return '/demandes';
  }

  // ── Règle 15 : /dossier → /personnel ─────────────────────────────────
  if (path === '/dossier' || path.startsWith('/dossier/')) {
    return '/personnel';
  }

  // Aucune règle correspondante : ne pas naviguer vers une URL API inconnue.
  return null;
}
